/**
 * /dashboard/settings/notifications — Settings → Communication →
 * Notification preferences (G9) + ICS calendar feed (G12).
 *
 * Per-user toggle matrix (category × channel: Email | In-app) backed by
 * PUT /api/v1/me/notification-preferences. The form component renders
 * skeleton rows while loading and surfaces a sonner toast "Preferences
 * saved" on success — both asserted by the e2e spec ST6.
 *
 * CalendarFeedCard (G12): lets any user generate / copy / revoke their
 * personal ICS feed token (POST/DELETE /api/v1/me/calendar-token).
 *
 * All users (any role) can manage both surfaces; no HR-admin gate (both
 * endpoints are scoped to the calling user).
 *
 * Migration note (2026-06-01, Next.js → Vite Phase 3g): replaces the
 * Coming soon placeholder ported from
 * frontend/src/app/(dashboard)/dashboard/settings/notifications/page.tsx.
 * G9 implementation: this commit. G12 CalendarFeedCard: Wave E.
 */

import { createFileRoute } from "@tanstack/react-router";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Bell } from "lucide-react";
import { NotificationPrefsForm } from "@/components/settings/notification-prefs-form";
import { CalendarFeedCard } from "@/components/settings/calendar-feed-card";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/notifications",
)({
  component: SettingsNotificationsRoute,
});

function SettingsNotificationsRoute() {
  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold">
              Notification preferences
            </h2>
            <p className="text-sm text-muted-foreground">
              Choose which channels receive each type of notification.
              Changes are per-user and take effect immediately.
            </p>
          </div>

          <Card className="max-w-3xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Bell aria-hidden className="h-4 w-4 text-primary" />
                Channel preferences
              </CardTitle>
              <CardDescription>
                All categories are enabled by default. Uncheck a cell to
                opt out of that channel for a specific event type.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <NotificationPrefsForm />
            </CardContent>
          </Card>

          <CalendarFeedCard />
        </div>
      </SettingsShell>
    </>
  );
}
